import { useEffect, useState } from "react";
import { Building2, MapPin } from "lucide-react";
import { cn } from "@/lib/cn";

// ============================================================================
// Customer 360 avatar — the visual anchor at the top of the header.
//
// Two states:
//   1. Initials over a deterministic gradient (default, no setup required).
//   2. Street View Static API thumbnail of the selected location's address,
//      when VITE_GOOGLE_MAPS_KEY is configured AND the customer has an
//      address. Falls back to initials on image error / no-imagery / no-key.
//
// Why rounded-square (not a circle): a circle reads as "this is a person"
// (matches our TechAvatar). A rounded-square reads as "this is a property
// / building / entity with locations" — the right mental model for a
// Customer record in an HVAC CRM where the entity could be a household,
// a business, or a property manager. Same visual language ServiceTitan,
// HCP, and HubSpot use for non-person account avatars.
//
// CRM-008 (Google Maps + caching + Maps-modal click) is the durable home
// for this feature. v1 here ships the *base* of CRM-008: initials path
// works today; Street View path activates the moment a key lands; the
// click-to-modal stub is left for later. Persistent per-property URL
// caching belongs on the `property` table after BUILD-17 ships
// DEC-NEW-04; until then a session-lifetime in-memory cache is enough.
// ============================================================================

export interface AvatarAddress {
  add1: string | null | undefined;
  city: string | null | undefined;
  state: string | null | undefined;
  zip: string | null | undefined;
}

interface Props {
  /** Drives the deterministic gradient + Street View cache key. */
  custno: string;
  /** Drives the initials fallback. */
  name: string;
  /** Address of the currently-selected location, if any. Null = initials. */
  address?: AvatarAddress | null;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

const sizeClasses: Record<NonNullable<Props["size"]>, string> = {
  sm: "h-10 w-10 text-sm",
  md: "h-12 w-12 text-base",
  lg: "h-16 w-16 text-xl",
  xl: "h-20 w-20 text-2xl",
};

// Same palette spirit as TechAvatar — brand-friendly, distinct hues so a
// dispatcher's eye can use color as a memory cue ("oh, the green-square
// customer"). The custno hash keeps it stable across sessions.
// Cool / jewel hues only — deliberately NO amber / orange / red. Those warm
// tones are the status vocabulary (amber = traveling, red = danger/alert), and
// a warm-square customer avatar can subconsciously read as "this customer is
// flagged." Keeping identity color cool lets the status palette own warm.
// (DS-05d, 2026-05-28.)
const gradientPalette = [
  "from-sky-500 to-blue-700",
  "from-blue-500 to-indigo-700",
  "from-indigo-500 to-violet-700",
  "from-violet-500 to-purple-700",
  "from-purple-500 to-fuchsia-700",
  "from-fuchsia-500 to-pink-700",
  "from-emerald-500 to-teal-700",
  "from-teal-500 to-cyan-700",
  "from-cyan-500 to-sky-700",
  "from-slate-500 to-slate-700",
] as const;

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function pickGradient(custno: string): string {
  return gradientPalette[hashString(custno) % gradientPalette.length];
}

// ----------------------------------------------------------------------------
// Entity-aware fallback (Seth, 2026-06-04): when there's no imagery, the
// fallback should KNOW what kind of entity it's looking at, not blindly take
// two initials. Future top of the chain: a customer-uploaded avatar from the
// customer portal (PORTAL-01) — portal photo → Street View → smart monogram.
// ----------------------------------------------------------------------------

const BUSINESS_WORDS =
  /\b(llc|inc|corp|co|company|companies|properties|property|propert|mgmt|management|apartments|apts|condos?|townhomes?|association|hoa|church|school|university|realty|real estate|group|center|centre|services?|bank|city of|county|partners|holdings|ventures|enterprises|office|dental|clinic|salon|cafe|restaurant|brewing|brewery|store|shop|auto|motors)\b/i;

export function isBusinessName(name: string): boolean {
  return BUSINESS_WORDS.test(name);
}

/**
 * Smart fallback text: businesses get a building glyph (handled by the
 * caller), multi-party households ("Moller & Barrett", "Doyle/Hockert") get
 * BOTH families' initials ("M·B"), a person gets plain initials.
 */
function getInitials(name: string): string {
  const multi = name.split(/\s*(?:&|\/| and )\s*/i).filter(Boolean);
  if (multi.length >= 2) {
    const firsts = multi
      .slice(0, 2)
      .map((part) => part.trim()[0])
      .filter(Boolean)
      .map((c) => c.toUpperCase());
    if (firsts.length === 2) return `${firsts[0]}·${firsts[1]}`;
  }
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}

/**
 * Build the Street View Static API URL for an address.
 * Returns null when there's no key or the address is too sparse to geocode.
 *
 * Size is intentionally 2x the rendered pixel size for retina sharpness.
 * fov=80 reads slightly wider than the default 90 — the property feels
 * "framed" rather than looking like a passing snapshot.
 */
export function buildStreetViewUrl(
  address: AvatarAddress | null | undefined,
  pixelSize: number,
): string | null {
  const key = import.meta.env.VITE_GOOGLE_MAPS_KEY;
  if (!key) return null;
  if (!address?.add1) return null;
  const parts = [
    address.add1,
    address.city,
    address.state,
    address.zip,
  ]
    .filter((s) => s && s.trim())
    .join(", ");
  if (!parts) return null;
  const params = new URLSearchParams({
    size: `${pixelSize * 2}x${pixelSize * 2}`,
    location: parts,
    fov: "80",
    pitch: "0",
    // No gray "no imagery" placeholder, ever — a 404 instead, so the <img>
    // fails and the caller falls back (HOMEOWNER-NAVY-01 r4: satellite, then nothing).
    return_error_code: "true",
    // Outdoor panoramas only — never a business interior that happens to sit
    // within the search radius.
    source: "outdoor",
    key,
  });
  return `https://maps.googleapis.com/maps/api/streetview?${params.toString()}`;
}

/** HOMEOWNER-NAVY-01 r4: the second choice when Street View has no imagery for
 *  the address — a satellite view of the roof with a small pin, same key. */
export function buildSatelliteUrl(
  address: AvatarAddress | null | undefined,
  width: number,
  height: number,
): string | null {
  const key = import.meta.env.VITE_GOOGLE_MAPS_KEY;
  if (!key) return null;
  if (!address?.add1) return null;
  const parts = [address.add1, address.city, address.state, address.zip].filter((s) => s && s.trim()).join(", ");
  if (!parts) return null;
  const params = new URLSearchParams({
    center: parts,
    zoom: "19",
    size: `${width}x${height}`,
    scale: "2",
    maptype: "satellite",
    markers: `size:mid|color:0xf97316|${parts}`,
    key,
  });
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

// Map our size keys to pixel sizes for the Street View URL.
const pixelSize: Record<NonNullable<Props["size"]>, number> = {
  sm: 40,
  md: 48,
  lg: 64,
  xl: 80,
};

export function CustomerAvatar({
  custno,
  name,
  address,
  size = "lg",
  className,
}: Props) {
  const [imageFailed, setImageFailed] = useState(false);

  const gradient = pickGradient(custno);
  const initials = getInitials(name);
  const streetViewUrl = buildStreetViewUrl(address, pixelSize[size]);
  const showImage = streetViewUrl && !imageFailed;

  // Reset failure state whenever the URL changes (location switcher click,
  // customer navigation). Without this, a customer with no Street View
  // imagery would stick imageFailed=true and the next customer with valid
  // imagery would still render the initials fallback.
  useEffect(() => {
    setImageFailed(false);
  }, [streetViewUrl]);

  return (
    <div
      className={cn(
        "flex flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br font-bold text-white shadow-sm ring-1 ring-black/5",
        gradient,
        sizeClasses[size],
        className,
      )}
      title={name}
    >
      {showImage ? (
        <img
          src={streetViewUrl!}
          alt={`${name} property`}
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
          loading="lazy"
        />
      ) : isBusinessName(name) ? (
        <Building2 className="h-1/2 w-1/2 opacity-90" aria-label={name} />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

/**
 * Wide Street View thumbnail for the location card (not the avatar). Falls
 * back to a quiet map-pin tile when there's no key / no imagery — never an
 * empty box.
 */
export function StreetViewThumb({
  address,
  className,
  designedFallback,
  onAvailability,
}: {
  address: AvatarAddress | null | undefined;
  className?: string;
  /** Kept for API compatibility — the fallback CHAIN is now street photo →
   *  satellite overhead → quiet pin tile (Seth 2026-06-11: "hate the blue
   *  image box… show an overhead view of that address instead"). */
  designedFallback?: { city?: string | null; zone?: string | null };
  /** Fires false when NEITHER street nor satellite imagery exists — the
   *  parent collapses the tile and reflows (Seth r3: no blank box, ever). */
  onAvailability?: (ok: boolean) => void;
}) {
  // The quiet tile renders IMMEDIATELY; imagery fades over it once loaded.
  // If Street View has no photo for the address, the OVERHEAD satellite shot
  // takes its place — nearly every address has one.
  const [streetLoaded, setStreetLoaded] = useState(false);
  const [streetFailed, setStreetFailed] = useState(false);
  const [satLoaded, setSatLoaded] = useState(false);
  const [satFailed, setSatFailed] = useState(false);
  const key = import.meta.env.VITE_GOOGLE_MAPS_KEY;
  const parts = [address?.add1, address?.city, address?.state, address?.zip]
    .filter((s) => s && String(s).trim())
    .join(", ");
  const streetUrl =
    key && address?.add1 && parts
      ? `https://maps.googleapis.com/maps/api/streetview?${new URLSearchParams({
          // HDR-PHOTO-02: max free-tier resolution + outdoor-only imagery
          // (no indoor photospheres / user uploads of the wrong thing).
          size: "640x400",
          location: parts,
          source: "outdoor",
          fov: "80",
          pitch: "0",
          return_error_code: "true",
          key,
        }).toString()}`
      : null;
  const satUrl =
    key && address?.add1 && parts
      ? `https://maps.googleapis.com/maps/api/staticmap?${new URLSearchParams({
          center: parts,
          zoom: "19",
          size: "640x400",
          scale: "2", // satellite supports retina — 1280×800 effective
          maptype: "satellite",
          key,
        }).toString()}`
      : null;
  useEffect(() => {
    setStreetLoaded(false);
    setStreetFailed(false);
    setSatLoaded(false);
    setSatFailed(false);
  }, [streetUrl]);

  // Some no-imagery addresses HANG instead of 404ing (seen live on
  // 0001071) — force the chain forward after 4s per stage so the tile
  // never sits gray.
  useEffect(() => {
    if (!streetUrl || streetLoaded || streetFailed) return;
    const t = setTimeout(() => setStreetFailed(true), 8000);
    return () => clearTimeout(t);
  }, [streetUrl, streetLoaded, streetFailed]);
  useEffect(() => {
    if (!streetFailed || !satUrl || satLoaded || satFailed) return;
    const t = setTimeout(() => setSatFailed(true), 8000);
    return () => clearTimeout(t);
  }, [streetFailed, satUrl, satLoaded, satFailed]);

  // Report imagery availability up: ok on first successful load; not-ok when
  // the whole chain is exhausted (or there's no key/address at all).
  useEffect(() => {
    if (!onAvailability) return;
    if (streetLoaded || satLoaded) onAvailability(true);
    else if (!streetUrl || (streetFailed && (satFailed || !satUrl))) onAvailability(false);
  }, [streetLoaded, satLoaded, streetFailed, satFailed, streetUrl, satUrl, onAvailability]);

  void designedFallback; // chain replaces the designed tile; prop kept benign

  return (
    <div
      className={cn(
        "relative flex items-center justify-center overflow-hidden bg-slate-100 text-slate-300",
        className,
      )}
    >
      <MapPin className="h-6 w-6" aria-hidden="true" />
      {streetFailed && satUrl && !satFailed && (
        <img
          src={satUrl}
          alt="Overhead view of the service address"
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
            satLoaded ? "opacity-100" : "opacity-0",
          )}
          onLoad={() => setSatLoaded(true)}
          onError={() => setSatFailed(true)}
        />
      )}
      {streetUrl && !streetFailed && (
        <img
          src={streetUrl}
          alt="Street view of the service address"
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
            streetLoaded ? "opacity-100" : "opacity-0",
          )}
          onLoad={() => setStreetLoaded(true)}
          onError={() => setStreetFailed(true)}
        />
      )}
    </div>
  );
}
